import { createReducer, on } from '@ngrx/store';
import {
	createEmptyOwnedStateResource,
	createEmptyStateResource,
	createOwnedStateResource,
	createStateResource,
	OwnedStateResource,
	StateResource,
} from '@schir-int-client/ngrx-helpers';
import {
	loadVerfahrenSingleSuccessAction,
	unassignKontaktSuccessAction,
} from 'libs/verfahren-shared/src/lib/verfahren.actions';
import {
	changeKontaktEditModeAction,
	createKontaktSuccessAction,
	getSearchKontakteOwner,
	loadKontakteByVerfahrenAction,
	loadKontakteByVerfahrenSuccessAction,
	searchKontakteAction,
	searchKontakteSuccessAction,
	setKontaktAssignmentSuccessAction,
	setSelectedKontaktAction,
	updateKontaktSuccessAction,
	updateVerfahrenKontaktFreiTextSuccessAction,
} from './adressverwaltung.actions';
import { KontaktInVerfahrenListResource, KontaktListResource, KontaktResource } from './adressverwaltung.model';
import { isNil } from 'lodash-es';
import { getEmbeddedResource } from '@ngxp/rest';
import { KontaktListLinkRel } from './adressverwaltung.linkrel';
import { sameUri } from '@schir-int-client/tech';
import { getIdNumber } from '../../../test-helpers/src/lib/resource-helper';

export interface AdressverwaltungState {
	kontakteBySearch: OwnedStateResource<KontaktListResource>;
	kontakteInVerfahren: StateResource<KontaktInVerfahrenListResource>;
	selectedKontakt: KontaktResource;
	editMode: boolean;
}

export const initialState: AdressverwaltungState = {
	kontakteBySearch: createEmptyOwnedStateResource(),
	kontakteInVerfahren: createEmptyStateResource(), // TODO: vielleicht auch owned?
	selectedKontakt: null,
	editMode: false,
};

export const adressverwaltungReducer = createReducer(
	initialState,
	on(searchKontakteAction, (state: AdressverwaltungState, { condition, filter, limit }): AdressverwaltungState => ({
		...state,
		kontakteBySearch: createEmptyOwnedStateResource(getSearchKontakteOwner(condition, filter, limit), true),
	})),
	on(searchKontakteSuccessAction, (state: AdressverwaltungState, { owner, kontakte }): AdressverwaltungState => ({
		...state,
		kontakteBySearch: createOwnedStateResource(owner, kontakte),
	})),
	on(loadVerfahrenSingleSuccessAction, (state: AdressverwaltungState): AdressverwaltungState => ({
		...state,
		kontakteInVerfahren: { ...state.kontakteInVerfahren, reload: true },
	})),
	on(loadKontakteByVerfahrenAction, (state: AdressverwaltungState): AdressverwaltungState => ({
		...state,
		kontakteInVerfahren: createEmptyStateResource(true),
	})),
	on(loadKontakteByVerfahrenSuccessAction, (state: AdressverwaltungState, { kontakte }): AdressverwaltungState => ({
		...state,
		kontakteInVerfahren: createStateResource(kontakte),
	})),
	on(
		setKontaktAssignmentSuccessAction,
		unassignKontaktSuccessAction,
		(state: AdressverwaltungState): AdressverwaltungState => ({
			...state,
			kontakteInVerfahren: { ...state.kontakteInVerfahren, reload: true },
		})
	),
	on(updateKontaktSuccessAction, (state: AdressverwaltungState, { kontakt }): AdressverwaltungState => ({
		...state,
		selectedKontakt: kontakt,
		kontakteInVerfahren: setReloadIfAffected(state.kontakteInVerfahren, getIdNumber(kontakt)),
		kontakteBySearch: setReloadIfFound(state.kontakteBySearch, kontakt),
	})),
	on(createKontaktSuccessAction, (state: AdressverwaltungState, { kontakt }): AdressverwaltungState => ({
		...state,
		selectedKontakt: kontakt,
		editMode: false,
	})),
	on(setSelectedKontaktAction, (state: AdressverwaltungState, { kontakt }): AdressverwaltungState => ({
		...state,
		selectedKontakt: kontakt,
		editMode: false,
	})),
	on(changeKontaktEditModeAction, (state: AdressverwaltungState, { editMode }): AdressverwaltungState => ({
		...state,
		editMode,
	})),
	on(
		updateVerfahrenKontaktFreiTextSuccessAction,
		(state: AdressverwaltungState, { verfahrenKontakt }): AdressverwaltungState => ({
			...state,
			kontakteInVerfahren: setReloadIfAffected(state.kontakteInVerfahren, verfahrenKontakt.kontaktId),
		})
	)
);

/**
 * Setzt das Reload-Flag auf KontakteInVerfahren, wenn geänderter Kontakt darin vorkommt.
 * @param kontakteInVerfahren
 * @param kontaktId
 */
function setReloadIfAffected(
	kontakteInVerfahren: StateResource<KontaktInVerfahrenListResource>,
	kontaktId: number
): StateResource<KontaktInVerfahrenListResource> {
	const list = kontakteInVerfahren.resource?._embedded?.kontaktInVerfahrenResList;
	if (isNil(list)) {
		return kontakteInVerfahren;
	}
	const affected = list.some(kiv => kiv.inVerfahren.kontaktId == kontaktId);
	return affected ? { ...kontakteInVerfahren, reload: true } : kontakteInVerfahren;
}

function setReloadIfFound(
	kontakteBySearch: OwnedStateResource<KontaktListResource>,
	kontakt: KontaktResource
): OwnedStateResource<KontaktListResource> {
	if (kontakteBySearch?.resource == null) {
		return kontakteBySearch;
	}
	const isContained = getEmbeddedResource<KontaktResource[]>(
		kontakteBySearch.resource,
		KontaktListLinkRel.KONTAKT_LIST
	).some(k => sameUri(k, kontakt));
	return isContained ? { ...kontakteBySearch, reload: true } : kontakteBySearch;
}
